import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
// BoltIcon import removed — the "Promote to typed PPA" button it powered is gone.
import ContractUpload from './ContractUpload';
import FairValueWorkbooks from './FairValueWorkbooks';
import InvoiceManagement from './InvoiceManagement';
import ContractDetail from './ContractDetail';
import ContractStatusChip from './ContractStatusChip';
import AssetChip from './AssetChip';
// ContractTimeline moved to the Term Sheet section (TermSheet.js) — the
// Documents list is a flat inventory of every uploaded artefact (contracts
// AND financial models), so a PPA-expiry timeline no longer belongs here.
import ContractAssistant from './ContractAssistant';
import FinancialModels from './FinancialModels';
// PromoteToTypedDialog removed — no longer needed in Firestore-typed model.
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

// New backend reports processing state under `contract.processing.status`.
// "ready" / "error" are terminal; anything else is in-flight.
const isProcessing = (procStatus) =>
  !!procStatus && !['ready', 'error'].includes(procStatus);

const formatDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// Human label per document kind. FINANCIAL_MODEL is the lender RevFM
// workbook routed to the financial-model engine (not the PDF pipeline).
const KIND_LABEL = {
  PPA: 'PPA',
  CAP: 'Cap',
  CIS: 'CIS',
  TOLLING: 'Tolling',
  FCAS: 'FCAS',
  ISDA: 'ISDA',
  FINANCIAL_MODEL: 'Financial Model',
};

// Chip palette: PPA = amber, Financial Model = green, everything else = blue.
const kindColors = (kind) => {
  if (kind === 'PPA') {
    return { bg: 'rgba(217, 138, 43, 0.08)', fg: '#D98A2B', bd: '1px solid rgba(217, 138, 43, 0.12)' };
  }
  if (kind === 'FINANCIAL_MODEL') {
    return { bg: 'rgba(34, 160, 90, 0.10)', fg: '#1f9d57', bd: '1px solid rgba(34, 160, 90, 0.22)' };
  }
  return { bg: 'rgba(59, 130, 246, 0.08)', fg: '#2d7dd2', bd: '1px solid rgba(59, 130, 246, 0.18)' };
};

const headerCellSx = {
  color: 'text.secondary',
  fontWeight: 700,
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
};

const ContractsDashboard = () => {
  const { portfolioId, contractId } = useParams();
  const navigate = useNavigate();
  const basePath = `/portfolios/${portfolioId}/contracts`;
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [assistantCollapsed, setAssistantCollapsed] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  // Bumped when a financial model is uploaded → remounts <FinancialModels>
  // so it refetches (models live in their own module, not /api/contracts).
  const [modelRefreshKey, setModelRefreshKey] = useState(0);
  // Bumped when an invoice is uploaded via the unified dialog → remounts the
  // embedded <InvoiceManagement> so it refetches.
  const [invoiceRefreshKey, setInvoiceRefreshKey] = useState(0);
  // Tab strip + FM count (reported up by the FinancialModels child so the
  // 'Financial Models (n)' badge stays in sync without a duplicate fetch).
  const [activeTab, setActiveTab] = useState('all');
  const [modelCount, setModelCount] = useState(0);
  const [fvCount, setFvCount] = useState(0);
  const [invoiceCount, setInvoiceCount] = useState(0);
  // promoteContract state removed alongside the obsolete Promote-to-typed workflow

  const fetchContracts = useCallback(async () => {
    try {
      // Scope the list to the current portfolio so the Contracts tab shows
      // only what's relevant. Top-level "all contracts" view is reachable
      // from a future /contracts URL (TBD) without this param.
      const params = portfolioId ? { portfolio_id: portfolioId } : {};
      const response = await axios.get(`${API_BASE_URL}/api/contracts`, { params });
      // Exclude any rows that are actually financial models — those live in
      // their own module (FinancialModels panel above) and shouldn't double
      // up here. Belt-and-braces filter: kind tag OR Excel extension OR
      // GCS path under financial-models/.
      const isFinancialModelRow = (c) => {
        if (c.contract_kind === 'FINANCIAL_MODEL') return true;
        const fname = (c.source?.filename || '').toLowerCase();
        if (/\.(xlsb|xlsm|xlsx)$/.test(fname)) return true;
        const uri = c.source?.pdf_uri || c.source?.gcs_uri || '';
        if (uri.includes('financial-models/')) return true;
        return false;
      };
      const filtered = (response.data.contracts || []).filter(
        (c) => !isFinancialModelRow(c),
      );
      setContracts(filtered);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  // Initial fetch
  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // Poll while any contract is still being processed by the backend pipeline.
  // (Direct Firestore listeners would push this live, but we keep all I/O
  // through the REST API — see /api/contracts.)
  useEffect(() => {
    const hasProcessing = contracts.some((c) => isProcessing(c.processing?.status));
    if (!hasProcessing) return;

    const interval = setInterval(fetchContracts, 3000);
    return () => clearInterval(interval);
  }, [contracts, fetchContracts]);

  const handleUploadComplete = useCallback(
    (payload, kind) => {
      if (kind === 'model') {
        // Financial model — lives in its own module; trigger a refetch of
        // the FinancialModels panel rather than the contracts table.
        setModelRefreshKey((k) => k + 1);
        return;
      }
      if (kind === 'invoice') {
        // AEMO settlement invoice — own module; refetch the invoices panel.
        setInvoiceRefreshKey((k) => k + 1);
        return;
      }
      setContracts((prev) => [payload, ...prev]);
    },
    []
  );

  const handleViewContract = useCallback((id) => {
    navigate(`${basePath}/${id}`);
  }, [navigate, basePath]);

  const handleBackToList = useCallback(() => {
    navigate(basePath);
    fetchContracts(); // Refresh list when returning
  }, [navigate, basePath, fetchContracts]);

  const handleDeleteClick = useCallback((e, contract) => {
    e.stopPropagation();
    setContractToDelete(contract);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!contractToDelete) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/api/contracts/${contractToDelete.contract_id}`
      );
      setContracts((prev) => prev.filter((c) => c.contract_id !== contractToDelete.contract_id));
    } catch (err) {
      setError('Failed to delete contract');
    } finally {
      setDeleteDialogOpen(false);
      setContractToDelete(null);
    }
  }, [contractToDelete]);

  const handleDeleteFromDetail = useCallback(
    (deletedId) => {
      setContracts((prev) => prev.filter((c) => c.contract_id !== deletedId));
      navigate(basePath);
    },
    [navigate, basePath]
  );

  // Detail View — when URL has a contractId param
  if (contractId) {
    return (
      <ContractDetail
        contractId={contractId}
        onBack={handleBackToList}
        onDelete={handleDeleteFromDetail}
      />
    );
  }

  // List View — full-width contract uploads list.
  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box sx={{ width: '100%', minWidth: 0 }}>
      {/* Header */}
      <Box className="animate-in" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <DescriptionIcon sx={{ fontSize: 26, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 700, letterSpacing: '-0.02em' }}>
          Documents
        </Typography>
        <Chip
          label={`${contracts.length} document${contracts.length !== 1 ? 's' : ''}`}
          size="small"
          sx={{
            bgcolor: 'background.default',
            color: 'text.secondary',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.72rem',
            fontWeight: 500,
            border: '1px solid', borderColor: 'divider',
          }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setUploadDialogOpen(true)}
          sx={{
            bgcolor: 'primary.main',
            color: '#F4F2EC',
            fontWeight: 600,
            px: 2.5,
            py: 0.8,
            boxShadow: '0 2px 8px rgba(217, 138, 43, 0.20)',
            '&:hover': {
              bgcolor: 'primary.dark',
              boxShadow: '0 4px 12px rgba(217, 138, 43, 0.30)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          Upload Document
        </Button>
      </Box>

      {/* Upload Dialog */}
      <ContractUpload
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUploadComplete={handleUploadComplete}
        portfolioId={portfolioId}
      />

      {/* Contract expiry timeline moved to the Term Sheet section. */}

      {/* Tab strip — splits the inventory into Contracts vs Financial
          Models so the page has a clear reading order. 'All' stacks
          both (contracts first, then financial models) for users who
          want the full picture without switching tabs. */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 2.5,
          minHeight: 38,
          borderBottom: '1px solid', borderColor: 'divider',
          '& .MuiTab-root': {
            minHeight: 38, textTransform: 'none',
            fontWeight: 600, fontSize: '0.85rem',
          },
        }}
      >
        <Tab value="all"       label={`All (${contracts.length + modelCount + fvCount + invoiceCount})`} />
        <Tab value="contracts" label={`Contracts (${contracts.length})`} />
        <Tab value="models"    label={`Financial Models (${modelCount})`} />
        <Tab value="fair-value" label={`Fair Value Workbooks (${fvCount})`} />
        <Tab value="invoices"  label={`Invoices (${invoiceCount})`} />
      </Tabs>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, border: '1px solid rgba(229, 56, 79, 0.2)' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress sx={{ color: 'primary.main' }} />
        </Box>
      )}

      {/* Contract Table — shown on All + Contracts tabs */}
      {!loading && contracts.length > 0
        && activeTab !== 'models' && activeTab !== 'fair-value'
        && activeTab !== 'invoices' && (
        <Box className="animate-in stagger-2">
          <TableContainer
            component={Paper}
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid', borderColor: 'divider',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(217, 138, 43, 0.08)' }}>
                  <TableCell sx={headerCellSx}>Filename</TableCell>
                  <TableCell sx={headerCellSx}>Type</TableCell>
                  <TableCell sx={headerCellSx}>Covers</TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell sx={headerCellSx}>Uploaded</TableCell>
                  <TableCell sx={headerCellSx} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contracts.map((contract, index) => (
                  <TableRow
                    key={contract.contract_id}
                    hover
                    onClick={() => handleViewContract(contract.contract_id)}
                    sx={{
                      cursor: 'pointer',
                      borderLeft: '2px solid transparent',
                      transition: 'all 0.2s ease',
                      animation: 'fadeInUp 0.4s ease-out both',
                      animationDelay: `${index * 0.04}s`,
                      '&:hover': {
                        bgcolor: 'rgba(217, 138, 43, 0.08)',
                        borderLeft: '2px solid #D98A2B',
                      },
                      '& td': {
                        borderBottom: '1px solid', borderBottomColor: 'divider',
                      },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <DescriptionIcon
                          sx={{ fontSize: 18, color: 'text.secondary', opacity: 0.5 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.primary',
                            fontWeight: 500,
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {contract.source?.filename || '(no filename)'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {contract.contract_kind && contract.contract_kind !== 'OTHER' ? (
                        (() => {
                          const c = kindColors(contract.contract_kind);
                          return (
                            <Chip
                              label={KIND_LABEL[contract.contract_kind] || contract.contract_kind}
                              size="small"
                              sx={{
                                bgcolor: c.bg,
                                color: c.fg,
                                fontWeight: 600,
                                fontSize: '0.72rem',
                                letterSpacing: '0.03em',
                                border: c.bd,
                              }}
                            />
                          );
                        })()
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {'\u2014'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Covered assets \u2014 prefer enriched name from backend.
                          Empty = either not yet processed against a portfolio,
                          or no match found. */}
                      {(() => {
                        const assets = contract.assets?.length > 0
                          ? contract.assets
                          : (contract.asset_ids || []).map(d => ({ asset_id: d, name: null }));
                        if (!assets.length) {
                          return <Typography variant="body2" color="text.secondary">{'\u2014'}</Typography>;
                        }
                        return (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                            {assets.slice(0, 3).map(a => (
                              <AssetChip key={a.asset_id} asset={a} compact />
                            ))}
                            {assets.length > 3 && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center' }}>
                                +{assets.length - 3}
                              </Typography>
                            )}
                          </Stack>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {/* Show pipeline status while in-flight, lifecycle status once ready.
                          Wrap the chip in a Tooltip when the pipeline errored
                          so the user can see WHY without opening DevTools. */}
                      <Tooltip
                        title={contract.processing?.status === 'error'
                          ? (contract.processing?.error || 'Processing failed — re-process to retry.')
                          : ''}
                        arrow placement="top"
                      >
                        <span>
                          <ContractStatusChip
                            status={
                              contract.processing?.status === 'ready'
                                ? (contract.status || 'ready')
                                : contract.processing?.status
                            }
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: '0.75rem',
                        }}
                      >
                        {formatDate(contract.source?.uploaded_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {/* "Promote to typed PPA" removed — Firestore contracts
                          are always typed at upload via terms.ppa / terms.cap /
                          etc., so this workflow is obsolete. */}
                      <Tooltip title="View details">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewContract(contract.contract_id);
                          }}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'primary.main' },
                            transition: 'color 0.2s ease',
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(e, contract)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'error.main' },
                            transition: 'color 0.2s ease',
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Contracts empty-state — only on All / Contracts tab, and only
          when there's no contract to list. The Models tab shows its own
          empty-state via the FinancialModels child below. */}
      {!loading && contracts.length === 0 && activeTab !== 'models'
        && activeTab !== 'fair-value' && activeTab !== 'invoices' && (
        <Paper
          className="animate-in stagger-2"
          sx={{
            p: 5,
            textAlign: 'center',
            bgcolor: 'background.paper',
            border: '1px solid', borderColor: 'divider',
            mb: 3,
          }}
        >
          <DescriptionIcon
            sx={{
              fontSize: 56,
              color: 'text.secondary',
              mb: 1.5,
              opacity: 0.3,
            }}
          />
          <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {activeTab === 'contracts'
              ? 'No contracts uploaded yet.'
              : 'No documents uploaded yet.'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, opacity: 0.7 }}>
            Upload a contract (PDF) or financial model (Excel) to get started.
          </Typography>
        </Paper>
      )}

      {/* Financial models — own module/schema. One is "active" and drives
          the Facility / DSCR / Variance screens. Rendered after contracts
          on the 'All' tab so the page reads top-to-bottom in a clear order;
          shown alone on the 'Financial Models' tab (where `embedded` hides
          the duplicate section header). */}
      {portfolioId && activeTab !== 'contracts' && activeTab !== 'fair-value'
        && activeTab !== 'invoices' && (
        <FinancialModels
          key={modelRefreshKey}
          portfolioId={portfolioId}
          embedded={activeTab === 'models'}
          onCountChange={setModelCount}
        />
      )}

      {/* Fair value workbooks — the EY embedded-derivative family. Own
          parser + registry (see fv_workbook.py); uploading the next
          measurement period's workbook here updates the PPAs & Contracts →
          Fair Value tab. */}
      {portfolioId && (activeTab === 'all' || activeTab === 'fair-value') && (
        <FairValueWorkbooks
          portfolioId={portfolioId}
          embedded={activeTab === 'fair-value'}
          onCountChange={setFvCount}
        />
      )}

      {/* AEMO settlement invoices — own module/schema (modules/invoices).
          Embedded (lean) panel here; the full visual tracker lives on the
          dedicated Invoices page under Contracts & Offtake. */}
      {portfolioId && (activeTab === 'all' || activeTab === 'invoices') && (
        <Box sx={{ mt: 4 }}>
          <InvoiceManagement
            key={invoiceRefreshKey}
            portfolioId={portfolioId}
            embedded
            onCountChange={setInvoiceCount}
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Document</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            Are you sure you want to delete "
            {contractToDelete?.source?.filename}"? This will remove the
            document and all associated data. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      </Box>

      {/* PromoteToTypedDialog removed — see /typed endpoint removal note. */}
    </Box>
  );
};

export default ContractsDashboard;
